/**
 * paymentService.ts
 *
 * Payment provider integration service
 * Handles Paystack and SnapScan payment initialization and verification
 */

import type {
  PaymentProvider,
  PaymentTransaction,
  PaystackInitResponse,
  SnapScanInitResponse,
  PaymentVerificationResponse,
  CustomerSyncRequest,
  CustomerSyncResponse,
  ExternalPaymentInitResponse,
  ExternalPaymentVerifyResponse,
  PlanDetails,
} from '../types/payment-types';
import type { AppConfig } from '../contexts/FormBuilderContext';
import { generatePaymentReference, sanitizePaymentMetadata } from '../utils/paymentUtils';

/**
 * After Paystack's openIframe(), poll briefly to find the injected popup
 * wrapper and set its z-index inline to ensure it sits above everything.
 */
function elevatePaystackPopup(): void {
  let attempts = 0;
  const maxAttempts = 20;

  const poll = () => {
    attempts++;
    const iframe = document.querySelector(
      'iframe[src*="paystack.com"], iframe[src*="checkout.paystack.com"]'
    );
    if (iframe) {
      // Elevate the iframe's parent wrapper
      const wrapper = iframe.closest('div') || iframe.parentElement;
      if (wrapper && wrapper !== document.body) {
        (wrapper as HTMLElement).style.zIndex = '2147483647';
        (wrapper as HTMLElement).style.position = 'fixed';
        console.log('✅ Elevated Paystack popup wrapper z-index');
      }
      return;
    }
    if (attempts < maxAttempts) {
      requestAnimationFrame(poll);
    }
  };

  requestAnimationFrame(poll);
}

/**
 * Initialize Paystack payment
 * Uses Paystack Inline.js for popup payment
 * @param publicKey Paystack public key
 * @param amount Amount in cents
 * @param email User's email address
 * @param metadata Payment metadata
 * @param onSuccess Callback function when payment succeeds
 * @param onClose Callback function when popup is closed
 * @param customerInfo Optional customer details (first_name, last_name, phone)
 * @returns Payment reference and access code
 */
export async function initPaystackPayment(
  publicKey: string,
  amount: number,
  email: string,
  metadata: Record<string, any>,
  onSuccess?: (response: any) => void,
  onClose?: () => void,
  customerInfo?: {
    first_name?: string;
    last_name?: string;
    phone?: string;
  },
  currency: string = 'ZAR'
): Promise<PaystackInitResponse> {
  try {
    // Validate inputs
    if (!publicKey || !publicKey.startsWith('pk_')) {
      throw new Error('Invalid Paystack public key');
    }

    if (!email || !email.includes('@')) {
      throw new Error('Valid email address is required for Paystack payment');
    }

    if (amount <= 0) {
      throw new Error('Payment amount must be greater than 0');
    }

    // Check if Paystack script is loaded
    if (typeof window === 'undefined' || !(window as any).PaystackPop) {
      throw new Error(
        'Paystack SDK not loaded. Please include Paystack Inline.js script in your HTML.'
      );
    }

    // Generate payment reference
    const reference = metadata.reference || generatePaymentReference(
      metadata.formId || 'FORM',
      metadata.fieldId || 'FIELD',
      metadata.sessionId
    );

    // Sanitize metadata for Paystack
    const sanitizedMetadata = sanitizePaymentMetadata(metadata);

    // Add body class to lower parent app z-index elements via CSS
    document.body.classList.add('paystack-popup-active');

    const popupCleanup = () => {
      document.body.classList.remove('paystack-popup-active');
    };

    // Initialize Paystack popup
    const PaystackPop = (window as any).PaystackPop;

    // Build configuration object with customer details if provided
    const paystackConfig: any = {
      key: publicKey,
      email: email,
      amount: amount, // Amount in kobo (cents)
      currency: currency,
      ref: reference,
      metadata: sanitizedMetadata,
      callback: function (response: any) {
        console.log('Paystack payment successful:', response);
        popupCleanup();
        if (onSuccess) {
          onSuccess(response);
        }
      },
      onClose: function () {
        console.log('Paystack payment cancelled');
        popupCleanup();
        if (onClose) {
          onClose();
        }
      },
    };

    // Add customer details if provided (Paystack will pre-fill these fields)
    // Note: Paystack uses 'firstname' and 'lastname' (no underscore)
    if (customerInfo) {
      if (customerInfo.first_name) {
        paystackConfig.firstname = customerInfo.first_name;
      }
      if (customerInfo.last_name) {
        paystackConfig.lastname = customerInfo.last_name;
      }
      if (customerInfo.phone) {
        paystackConfig.phone = customerInfo.phone;
      }
    }

    // Debug: Log the complete Paystack configuration
    console.log('🔍 Paystack Configuration Being Sent:', {
      ...paystackConfig,
      key: paystackConfig.key.substring(0, 10) + '...', // Mask key for security
      callback: '[Function]',
      onClose: '[Function]',
    });

    const handler = PaystackPop.setup(paystackConfig);

    // Open payment popup
    handler.openIframe();

    // Elevate the popup wrapper above parent app elements
    elevatePaystackPopup();

    return {
      reference,
      accessCode: reference, // Paystack uses reference as access code
    };
  } catch (error) {
    console.error('Paystack initialization error:', error);
    throw new Error(
      `Failed to initialize Paystack payment: ${(error as Error).message}`
    );
  }
}

/**
 * Open Paystack popup using an access_code obtained from backend/webhook.
 * Used for external payment mode and plan-based built-in payments where
 * the backend initializes the transaction and returns an access_code.
 *
 * When planCode is provided, uses `plan: planCode` instead of `access_code`.
 * This is required for plan/subscription transactions — Paystack's Inline SDK
 * crashes with "Cannot destructure property 'language' of 'object null'" when
 * `access_code` from a plan transaction is passed to PaystackPop.setup().
 */
export function openPaystackPopupWithAccessCode(
  publicKey: string,
  accessCode: string,
  email: string,
  amount: number,
  currency: string = 'ZAR',
  onSuccess?: (response: any) => void,
  onClose?: () => void,
  planCode?: string,
  reference?: string
): void {
  if (typeof window === 'undefined' || !(window as any).PaystackPop) {
    throw new Error(
      'Paystack SDK not loaded. Please include Paystack Inline.js script in your HTML.'
    );
  }

  // Add body class to lower parent app z-index elements via CSS
  document.body.classList.add('paystack-popup-active');

  const cleanup = () => {
    document.body.classList.remove('paystack-popup-active');
  };

  const PaystackPop = (window as any).PaystackPop;

  // Unified configuration object
  try {
    const paystack = new PaystackPop();

    if (planCode) {
      // Plan/subscription mode
      console.log('🔑 Paystack V2: Starting new plan transaction', { plan: planCode });
      paystack.newTransaction({
        key: publicKey,
        email: email,
        amount: amount,
        currency: currency,
        plan: planCode,
        ref: reference,
        onSuccess: (response: any) => {
          console.log('✅ Paystack V2 Success:', response);
          cleanup();
          onSuccess?.(response);
        },
        onCancel: () => {
          console.log('ℹ️ Paystack V2 Cancelled');
          cleanup();
          onClose?.();
        }
      });
    } else if (accessCode) {
      // Resume mode - The official V2 way to resume a backend-initialized transaction
      console.log('🔑 Paystack V2: Resuming transaction via access_code', accessCode);
      paystack.resumeTransaction(accessCode, {
        onSuccess: (response: any) => {
          console.log('✅ Paystack V2 Resume Success:', response);
          cleanup();
          onSuccess?.(response);
        },
        onCancel: () => {
          console.log('ℹ️ Paystack V2 Resume Cancelled');
          cleanup();
          onClose?.();
        }
      });
    } else {
      // Standard transaction mode
      console.log('🔑 Paystack V2: Starting standard transaction');
      paystack.newTransaction({
        key: publicKey,
        email: email,
        amount: amount,
        currency: currency,
        ref: reference,
        onSuccess: (response: any) => {
          console.log('✅ Paystack V2 Success:', response);
          cleanup();
          onSuccess?.(response);
        },
        onCancel: () => {
          console.log('ℹ️ Paystack V2 Cancelled');
          cleanup();
          onClose?.();
        }
      });
    }

    // Elevate the popup wrapper (V2 still uses an iframe wrapper)
    elevatePaystackPopup();
  } catch (error) {
    console.error('❌ Paystack V2 Error:', error);
    cleanup();
    throw error;
  }
}

/**
 * Initialize SnapScan payment
 * Generates QR code for SnapScan mobile app
 * @param merchantId SnapScan merchant ID
 * @param amount Amount in cents
 * @param metadata Payment metadata
 * @returns QR code URL and payment reference
 */
export async function initSnapScanPayment(
  merchantId: string,
  amount: number,
  metadata: Record<string, any>
): Promise<SnapScanInitResponse> {
  try {
    // Validate inputs
    if (!merchantId) {
      throw new Error('SnapScan merchant ID is required');
    }

    if (amount <= 0) {
      throw new Error('Payment amount must be greater than 0');
    }

    // Generate payment reference
    const reference = metadata.reference || generatePaymentReference(
      metadata.formId || 'FORM',
      metadata.fieldId || 'FIELD',
      metadata.sessionId
    );

    // SnapScan QR code format
    // Format: snapscan://pay?merchant={merchantId}&amount={amount}&reference={reference}
    const qrData = `snapscan://pay?merchant=${merchantId}&amount=${(amount / 100).toFixed(2)}&reference=${reference}`;

    // Generate QR code URL using a QR code service
    // You can use services like api.qrserver.com or implement your own
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrData)}`;

    return {
      reference,
      qrCodeUrl,
      qrData, // Include raw data for custom QR generation if needed
    };
  } catch (error) {
    console.error('SnapScan initialization error:', error);
    throw new Error(
      `Failed to initialize SnapScan payment: ${(error as Error).message}`
    );
  }
}

/**
 * Verify payment completion with backend
 * @param reference Payment reference
 * @param provider Payment provider
 * @param backendUrl Backend API base URL
 * @returns Payment verification result
 */
export async function verifyPayment(
  reference: string,
  provider: PaymentProvider,
  backendUrl: string,
  applicationId: string,
  formVersionId?: number | string
): Promise<PaymentVerificationResponse> {
  try {
    if (!reference) {
      throw new Error('Payment reference is required for verification');
    }

    if (!backendUrl) {
      throw new Error('Backend URL is required for payment verification');
    }

    if (!applicationId) {
      throw new Error('Application ID is required for payment verification');
    }

    // Call backend API to verify payment
    const response = await fetch(`${backendUrl}/api/v1/payments/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        reference,
        provider,
        application_id: applicationId,
        form_version_id: formVersionId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Verification request failed: ${response.statusText}`);
    }

    const result = await response.json();

    if (result.code === 200 && result.data) {
      return {
        success: true,
        transaction: result.data.transaction,
      };
    } else {
      return {
        success: false,
        error: result.message || 'Payment verification failed',
      };
    }
  } catch (error) {
    console.error('Payment verification error:', error);
    return {
      success: false,
      error: `Payment verification failed: ${(error as Error).message}`,
    };
  }
}

/**
 * Validate payment configuration exists and is valid
 * @param appConfig Application configuration
 * @returns Validation result
 */
export function validatePaymentConfig(
  appConfig: AppConfig | null
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!appConfig) {
    errors.push('Application configuration not loaded');
    return { valid: false, errors };
  }

  if (!appConfig.features || !appConfig.features.payment) {
    errors.push('Payment configuration not found in app config');
    return { valid: false, errors };
  }

  const paymentConfig = appConfig.features.payment;

  // Check if at least one payment provider is enabled
  const hasPaystack = paymentConfig.paystackEnabled && paymentConfig.paystackPublicKey;
  const hasSnapScan = paymentConfig.snapScanEnabled && paymentConfig.snapScanConfig?.merchantId;

  if (!hasPaystack && !hasSnapScan) {
    errors.push('No payment providers are enabled and configured');
  }

  // Validate Paystack config if enabled
  if (paymentConfig.paystackEnabled) {
    if (!paymentConfig.paystackPublicKey) {
      errors.push('Paystack is enabled but public key is missing');
    } else if (!paymentConfig.paystackPublicKey.startsWith('pk_')) {
      errors.push('Invalid Paystack public key format (should start with pk_)');
    }
  }

  // Validate SnapScan config if enabled
  if (paymentConfig.snapScanEnabled) {
    if (!paymentConfig.snapScanConfig?.merchantId) {
      errors.push('SnapScan is enabled but merchant ID is missing');
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Poll payment status until completion or timeout
 * Useful for SnapScan which requires polling
 * @param reference Payment reference
 * @param provider Payment provider
 * @param backendUrl Backend API base URL
 * @param maxAttempts Maximum polling attempts (default: 60)
 * @param intervalMs Polling interval in milliseconds (default: 2000)
 * @returns Payment transaction when completed
 */
export async function pollPaymentStatus(
  reference: string,
  provider: PaymentProvider,
  backendUrl: string,
  applicationId: string,
  maxAttempts: number = 60,
  intervalMs: number = 2000
): Promise<PaymentTransaction> {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const poll = async () => {
      attempts++;

      try {
        const result = await verifyPayment(reference, provider, backendUrl, applicationId);

        if (result.success && result.transaction) {
          const status = result.transaction.status;

          if (status === 'completed') {
            resolve(result.transaction);
            return;
          } else if (status === 'failed' || status === 'cancelled') {
            reject(new Error(`Payment ${status}`));
            return;
          }
        }

        // Continue polling if not completed and under max attempts
        if (attempts < maxAttempts) {
          setTimeout(poll, intervalMs);
        } else {
          reject(new Error('Payment verification timeout'));
        }
      } catch (error) {
        reject(error);
      }
    };

    poll();
  });
}

/**
 * Initialize Paystack payment with redirect flow
 * Calls backend to get authorization URL and redirects user to Paystack hosted page
 *
 * @param amount Amount in cents
 * @param email User's email address
 * @param metadata Payment metadata
 * @param callbackUrl URL to redirect to after payment
 * @param customerInfo Customer details (first_name, last_name, phone)
 * @param apiBaseUrl Backend API base URL
 * @param currency Currency code (default: ZAR)
 * @returns Authorization URL and reference
 */
export async function initPaystackPaymentRedirect(
  amount: number,
  email: string,
  metadata: Record<string, any>,
  callbackUrl: string,
  customerInfo: {
    first_name?: string;
    last_name?: string;
    phone?: string;
  },
  apiBaseUrl: string,
  currency: string = 'ZAR',
  formVersionId?: number | string,
  planCode?: string
): Promise<{
  authorization_url: string;
  access_code: string;
  reference: string;
}> {
  try {
    // Validate inputs
    if (!email || !email.includes('@')) {
      throw new Error('Valid email address is required for Paystack payment');
    }

    if (amount <= 0) {
      throw new Error('Payment amount must be greater than 0');
    }

    if (!callbackUrl) {
      throw new Error('Callback URL is required for payment redirect');
    }

    // Generate payment reference
    const reference = metadata.reference || generatePaymentReference(
      metadata.formId || 'FORM',
      metadata.fieldId || 'FIELD',
      metadata.sessionId
    );

    // Sanitize metadata for Paystack
    const sanitizedMetadata = sanitizePaymentMetadata(metadata);

    // Call backend to initialize payment
    const response = await fetch(`${apiBaseUrl}/api/v1/payments/initialize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        amount,
        currency,
        callback_url: callbackUrl,
        reference,
        metadata: sanitizedMetadata,
        first_name: customerInfo.first_name,
        last_name: customerInfo.last_name,
        phone: customerInfo.phone,
        application_id: metadata.application_id || 'app-12345',
        form_version_id: formVersionId || metadata.form_version_id,
        ...(planCode ? { plan: planCode } : {}),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to initialize payment');
    }

    const result = await response.json();

    if (result.code !== 200 || !result.data) {
      throw new Error(result.message || 'Payment initialization failed');
    }

    return {
      authorization_url: result.data.authorization_url,
      access_code: result.data.access_code,
      reference: result.data.reference,
    };
  } catch (error) {
    console.error('Payment initialization error:', error);
    throw error;
  }
}

/**
 * Initialize payment via an external webhook
 * POSTs customer/payment data to the webhook URL and expects back
 * { access_code, reference } for Paystack Inline V2 resumeTransaction().
 *
 * @param webhookUrl External webhook URL
 * @param data Payment and customer data to send
 * @returns access_code and reference from the webhook
 */
export async function initExternalPayment(
  webhookUrl: string,
  data: Record<string, any>
): Promise<ExternalPaymentInitResponse> {
  try {
    if (!webhookUrl) {
      throw new Error('Webhook URL is required for external payment mode');
    }

    // Email may be at top level (legacy) or nested inside answers (page-submission structure)
    const email = data.email || data.answers?.email;
    if (!email || !email.includes('@')) {
      throw new Error('Valid email address is required');
    }

    console.log('🔗 Initializing external payment via webhook:', webhookUrl);

    const requestBody = data;

    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      console.error(`External payment webhook error: status ${response.status}`, errorText);
      throw new Error('Payment initialization failed. Please try again.');
    }

    const raw = await response.json();
    console.log('📦 Raw n8n response:', JSON.stringify(raw));
    // n8n may return a single object or wrap it in an array — handle both
    const result = Array.isArray(raw) ? raw[0] : raw;
    console.log('📦 Extracted result keys:', Object.keys(result ?? {}));
    console.log('📦 result.access_code:', result?.access_code);

    // Check for explicit failure response from webhook (e.g. SUBSCRIPTION_EXISTS)
    if (result?.success === false) {
      const errorCode = result.error || 'PAYMENT_ERROR';
      const errorMessage = result.message || 'Payment initialization failed. Please try again.';
      console.error('External payment webhook returned error:', errorCode, errorMessage);
      throw new Error(errorMessage);
    }

    // Active subscription — no payment required, return bypass signal
    if (result?.requires_payment === false && result?.success) {
      console.log('🎫 Webhook indicates active subscription — no payment popup required');
      return {
        access_code: '',
        reference: result.subscription_code || '',
        requires_payment: false,
        subscription_code: result.subscription_code,
        status: result.status || 'active',
        _rawWebhookResult: result,
      };
    }

    // Validate the expected response shape
    if (!result?.access_code || !result?.reference) {
      console.error('External payment webhook returned incomplete response:', Object.keys(result ?? {}));
      throw new Error('Payment initialization failed. Please try again.');
    }

    console.log('✅ External payment initialized, access_code received');

    return {
      access_code: result.access_code,
      reference: result.reference,
      authorization_url: result.authorization_url,
    };
  } catch (error) {
    console.error('External payment initialization error:', error);
    // Rethrow as-is so webhook-specific messages (e.g. SUBSCRIPTION_EXISTS) reach the UI
    throw error;
  }
}

/**
 * Verify an external payment via the provider's verification endpoint
 * POSTs { reference } to the verification URL and expects an ExternalPaymentVerifyResponse.
 * If no verification URL is provided, returns a 'pending' status (trust-but-verify pattern).
 *
 * @param verificationUrl External verification endpoint URL (optional)
 * @param reference Payment reference to verify
 * @returns ExternalPaymentVerifyResponse
 */
export async function verifyExternalPayment(
  verificationUrl: string | undefined,
  reference: string
): Promise<ExternalPaymentVerifyResponse> {
  // If no verification URL, accept as pending
  if (!verificationUrl) {
    console.log('ℹ️ No external verification URL configured, accepting payment as pending');
    return { status: 'pending', reference };
  }

  try {
    console.log('🔍 Verifying external payment via:', verificationUrl);

    const response = await fetch(verificationUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reference }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      console.error(`External verification error: status ${response.status}`, errorText);
      throw new Error('Verification request failed');
    }

    const result = await response.json();

    // Validate the expected response shape
    if (!result.status) {
      console.error('External verification returned incomplete response:', Object.keys(result));
      throw new Error('Invalid verification response');
    }

    return {
      status: result.status,
      reference: result.reference || reference,
      transactionId: result.transactionId,
      error: result.error,
    };
  } catch (error) {
    console.error('External payment verification error:', error);
    return {
      status: 'failed',
      reference,
      error: 'Payment verification failed. Please contact support.',
    };
  }
}

/**
 * Fetch plan details from Paystack via the backend
 * Used to resolve the amount for plan-based payments before displaying to the user.
 *
 * @param planCode Paystack plan code (e.g., "PLN_xxxxx")
 * @param apiBaseUrl Backend API base URL
 * @returns PlanDetails or null if fetch fails
 */
export async function fetchPlanDetails(
  planCode: string,
  apiBaseUrl: string
): Promise<PlanDetails | null> {
  try {
    if (!planCode || !apiBaseUrl) {
      return null;
    }

    console.log('📋 Fetching plan details for:', planCode);

    const response = await fetch(`${apiBaseUrl}/api/v1/payments/plans/${encodeURIComponent(planCode)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.warn(`Failed to fetch plan details: ${response.status}`);
      return null;
    }

    const result = await response.json();

    if (result.code === 200 && result.data) {
      console.log('✅ Plan details fetched:', result.data.name, result.data.amount);
      return result.data as PlanDetails;
    }

    return null;
  } catch (error) {
    console.warn('Failed to fetch plan details:', error);
    return null;
  }
}
